<template>
  <b-row>
    <b-col lg="12">
      <b-button
        v-if="$can('quick_link_create') || $can('resource_create') || $can('Zircly Admin') || $can('HR')"
        class="my-1 mobile-responsive"
        style="float: right"
        v-ripple.400="'rgba(113, 102, 240, 0.15)'"
        variant="primary"
        v-b-modal.modal-add-resource
      >
        <span class="align-middle">Add Resource</span>
      </b-button>
    </b-col>
    <b-col lg="12" md="12">
      <b-card>
        <b-table
          responsive
          :items="resources"
          :fields="tableColumns"
          show-empty
          empty-text="No resources found"
        >
          <template #cell(name)="data">
            <b-link @click="openResource(data.item)">
              <feather-icon
                :icon="data.item.type === 'link' ? 'LinkIcon' : 'FileTextIcon'"
                class="mr-50"
              />
              {{ data.item.name }}
            </b-link>
          </template>

          <template #cell(type)="data">
            <span class="text-capitalize">{{ data.item.type }}</span>
          </template>

          <template #cell(status)="data">
            <b-badge pill :variant="`light-${statusVariant(data.item.status)}`">
              {{ data.item.status }}
            </b-badge>
          </template>

          <template #cell(created_by)="data">
            {{ data.item.created_by || "-" }}
          </template>

          <template #cell(updated_at)="data">
            {{ data.item.updated_at | formatDate }}
          </template>

          <template #cell(actions)="data">
            <feather-icon
              icon="EyeIcon"
              color="primary"
              class="mr-1 cursor-pointer"
              v-b-tooltip.hover
              title="View"
              @click="openResource(data.item)"
            />
            <feather-icon
              v-if="$can('quick_link_edit') || $can('resource_edit') || $can('Zircly Admin') || $can('HR')"
              icon="Edit2Icon"
              color="green"
              class="mr-1 cursor-pointer"
              v-b-modal.modal-edit-resource
              @click="editResource(data.item)"
            />
            <feather-icon
              v-if="$can('quick_link_delete') || $can('resource_delete') || $can('Zircly Admin') || $can('HR')"
              icon="TrashIcon"
              color="red"
              class="cursor-pointer"
              @click="removeResource(data.item)"
            />
          </template>
        </b-table>
      </b-card>
    </b-col>

    <!-- Add Resource -->
    <b-modal
      id="modal-add-resource"
      ref="modal-add-resource"
      title="Add Resource"
      ok-title="submit"
      @ok="formSubmitted"
      @show="resetModal"
      @hidden="resetModal"
      cancel-variant="outline-danger"
    >
      <b-overlay :show="isBusy" rounded="sm">
        <b-form>
          <b-form-group label="Name" label-for="name">
            <b-form-input
              id="name"
              placeholder="Enter name"
              v-model="new_resource.name"
            />
          </b-form-group>
          <b-form-group label="Type">
            <b-form-radio-group
              v-model="new_resource.type"
              :options="typeOptions"
            />
          </b-form-group>
          <b-form-group
            v-if="new_resource.type === 'link'"
            label="Enter Link/URL"
            label-for="url"
          >
            <b-form-input
              id="url"
              placeholder="Enter Link/URL"
              v-model="new_resource.url"
            />
          </b-form-group>
          <b-form-group
            v-else
            label="Upload Document (PDF, DOCX or XLSX, max 10MB)"
            label-for="file"
          >
            <b-form-file
              id="file"
              v-model="new_resource.file"
              accept=".pdf,.docx,.xlsx"
              placeholder="Choose a file..."
            />
          </b-form-group>
        </b-form>
      </b-overlay>
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="outline-danger" @click="cancel()"> Cancel </b-button>
        <b-button :disabled="isBusy" variant="success" @click="ok()">
          <b-spinner v-if="isBusy" small class="mr-50"></b-spinner>
          Submit
        </b-button>
      </template>
    </b-modal>

    <!-- Edit Resource -->
    <b-modal
      id="modal-edit-resource"
      ref="modal-edit-resource"
      title="Edit Resource"
      ok-title="submit"
      @ok="formUpdated"
      cancel-variant="outline-danger"
    >
      <b-overlay :show="isBusy" rounded="sm">
        <b-form>
          <b-form-group label="Name" label-for="edit-name">
            <b-form-input id="edit-name" v-model="selected_resource.name" />
          </b-form-group>
          <b-form-group
            v-if="selected_resource.type === 'link'"
            label="Enter Link/URL"
            label-for="edit-url"
          >
            <b-form-input id="edit-url" v-model="selected_resource.url" />
          </b-form-group>
          <b-form-group
            v-else
            label="Replace Document (optional, PDF/DOCX/XLSX, max 10MB)"
            label-for="edit-file"
          >
            <b-form-file
              id="edit-file"
              v-model="selected_resource.file"
              accept=".pdf,.docx,.xlsx"
              placeholder="Choose a file to replace the current one..."
            />
          </b-form-group>
        </b-form>
      </b-overlay>
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="outline-danger" @click="cancel()"> Cancel </b-button>
        <b-button :disabled="isBusy" variant="success" @click="ok()">
          <b-spinner v-if="isBusy" small class="mr-50"></b-spinner>
          Update
        </b-button>
      </template>
    </b-modal>
  </b-row>
</template>


<script>
import {
  BRow,
  BCol,
  BLink,
  BCard,
  BFormGroup,
  BFormInput,
  BFormFile,
  BFormRadioGroup,
  BTable,
  BBadge,
  BForm,
  BButton,
  BOverlay,
  BSpinner,
  VBModal,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";

import axios from "@axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import Vue from "vue";
import VueCookies from "vue-cookies";
Vue.use(VueCookies);

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BCard,
    BFormGroup,
    BFormInput,
    BFormFile,
    BFormRadioGroup,
    BTable,
    BBadge,
    BForm,
    BButton,
    BOverlay,
    BSpinner,
    VBModal,
  },
  directives: {
    "b-modal": VBModal,
    Ripple,
  },
  filters: {
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString();
    },
  },
  data() {
    return {
      isBusy: false,
      resources: [],
      tableColumns: [
        { key: "name", label: "Name" },
        { key: "type", label: "Type" },
        { key: "status", label: "Status" },
        { key: "created_by", label: "Created By" },
        { key: "updated_at", label: "Updated At" },
        { key: "actions", label: "Actions" },
      ],
      typeOptions: [
        { text: "Link", value: "link" },
        { text: "Document", value: "document" },
      ],
      new_resource: {
        name: "",
        type: "link",
        url: "",
        file: null,
      },
      selected_resource: {
        hashid: "",
        name: "",
        type: "link",
        url: "",
        file: null,
      },
      userData: this.$cookies.get("userData"),
    };
  },
  created() {
    this.getResources();
  },
  methods: {
    statusVariant(status) {
      const map = {
        active: "primary",
        indexed: "success",
        parsed: "info",
        pending: "warning",
        failed: "danger",
      };
      return map[status] || "secondary";
    },
    resetModal() {
      this.new_resource = {
        name: "",
        type: "link",
        url: "",
        file: null,
      };
    },
    notify(title, variant) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title,
            icon: "EditIcon",
            variant,
            hideClose: true,
          },
        },
        {
          timeout: 2000,
          position: "bottom-center",
          toastClassName:
            variant === "danger" ? "radius-8 error-info" : "successful-info",
          hideProgressBar: true,
        }
      );
    },
    formSubmitted(bvModalEvt) {
      bvModalEvt.preventDefault();
      if (this.new_resource.name === "") {
        this.notify("Name is Required", "danger");
        return;
      }
      if (this.new_resource.type === "link" && !this.new_resource.url) {
        this.notify("Link URL is Required", "danger");
        return;
      }
      if (this.new_resource.type === "document" && !this.new_resource.file) {
        this.notify("A document file is required", "danger");
        return;
      }

      const formData = new FormData();
      formData.append("name", this.new_resource.name);
      formData.append("type", this.new_resource.type);
      if (this.new_resource.type === "link") {
        formData.append("url", this.new_resource.url);
      } else {
        formData.append("file", this.new_resource.file);
      }

      this.isBusy = true;
      axios
        .post("/resources", formData)
        .then((response) => {
          this.isBusy = false;
          if (response.status === 201) {
            this.getResources();
            this.$refs["modal-add-resource"].hide();
            this.notify("Resource created successfully", "success");
          }
        })
        .catch((error) => {
          this.isBusy = false;
          for (const err in error.response.data.errors) {
            this.notify(error.response.data.errors[err][0], "danger");
          }
        });
    },
    editResource(resource) {
      this.selected_resource = {
        hashid: resource.hashid,
        name: resource.name,
        type: resource.type,
        url: resource.url,
        file: null,
      };
    },
    formUpdated(bvModalEvt) {
      bvModalEvt.preventDefault();
      if (this.selected_resource.name === "") {
        this.notify("Name is Required", "danger");
        return;
      }
      if (
        this.selected_resource.type === "link" &&
        !this.selected_resource.url
      ) {
        this.notify("Link URL is Required", "danger");
        return;
      }

      const formData = new FormData();
      formData.append("name", this.selected_resource.name);
      formData.append("type", this.selected_resource.type);
      formData.append("_method", "PUT");
      if (this.selected_resource.type === "link") {
        formData.append("url", this.selected_resource.url);
      } else if (this.selected_resource.file) {
        formData.append("file", this.selected_resource.file);
      }

      this.isBusy = true;
      axios
        .post("/resources/" + this.selected_resource.hashid, formData)
        .then((response) => {
          this.isBusy = false;
          if (response.status === 202) {
            this.getResources();
            this.$refs["modal-edit-resource"].hide();
            this.notify("Resource updated successfully", "success");
          }
        })
        .catch((error) => {
          this.isBusy = false;
          for (const err in error.response.data.errors) {
            this.notify(error.response.data.errors[err][0], "danger");
          }
        });
    },
    getResources() {
      axios.get("/resources").then((response) => {
        if (response.data.success) {
          this.resources = response.data.data;
        }
      });
    },
    redirect(url) {
      window.open(url, "_blank");
    },
    openResource(resource) {
      const targetUrl = resource.type === "link" ? resource.url : resource.file_url;
      if (targetUrl) {
        window.open(targetUrl, "_blank");
      }
    },
    removeResource(resource) {
      this.$swal({
        title: "Are you sure?",
        text: `You want to delete this resource - ${resource.name}. This cannot be undone.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          axios
            .delete("/resources/" + resource.hashid)
            .then((response) => {
              if (response.status === 204) {
                this.getResources();
                this.notify("Resource deleted successfully", "success");
              }
            })
            .catch((error) => {
              for (const err in error.response.data.errors) {
                this.notify(error.response.data.errors[err][0], "danger");
              }
            });
        }
      });
    },
  },
};
</script>
